import React, { useState, useEffect } from 'react';
import {
  Upload,
  Save,
  GripVertical,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Layout,
  Type,
  Link as LinkIcon,
  Image as ImageIcon,
  MousePointer2
} from 'lucide-react';
import {
  databaseService,
  storageService,
  appwriteConfig,
  ID,
} from '../../../lib/appwrite';
import FontSelector from '../../../components/FontSelector/FontSelector';

export default function Menubar() {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuItemsFont, setMenuItemsFont] = useState("'Poppins', sans-serif");
  const [cvButton, setCvButton] = useState({ text: 'Get My CV', link: '#' });
  const [cvButtonFont, setCvButtonFont] = useState("'Poppins', sans-serif");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const MENUBAR_COLLECTION = 'menubar_settings';
  const STORAGE_BUCKET = appwriteConfig.storageId || 'reactbucket';

  useEffect(() => {
    loadMenubarSettings();
  }, []);

  const loadMenubarSettings = async () => {
    try {
      setLoading(true);
      const menuResult = await databaseService.listDocuments(MENUBAR_COLLECTION);
      if (menuResult.success && menuResult.data.documents.length > 0) {
        const settings = menuResult.data.documents[0];
        if (settings.menuItems) setMenuItems(JSON.parse(settings.menuItems));
        if (settings.menuItemsFont) setMenuItemsFont(settings.menuItemsFont);
        if (settings.logoUrl) {
          setLogoUrl(settings.logoUrl);
          setLogoPreview(settings.logoUrl);
        }
        if (settings.cvButton) setCvButton(JSON.parse(settings.cvButton));
        if (settings.cvButtonFont) setCvButtonFont(settings.cvButtonFont);
      } else {
        const defaultItems = [
          { id: '1', name: 'Home', href: '/', enabled: true, order: 0 },
          { id: '2', name: 'About Me', href: '/about', enabled: true, order: 1 },
          { id: '3', name: 'Project', href: '/projects', enabled: true, order: 2 },
          { id: '4', name: 'Spatial Canvas', href: '/spatial-canvas', enabled: true, order: 3 },
          { id: '5', name: 'Certificate', href: '/certificates', enabled: true, order: 4 },
          { id: '6', name: 'Blog', href: '/blogs', enabled: true, order: 5 },
          { id: '7', name: 'Experience', href: '/about#experience', enabled: true, order: 6 },
          { id: '8', name: 'Galleries', href: '/project-gallery', enabled: true, order: 7 },
          { id: '9', name: 'Shop', href: '/shop', enabled: true, order: 8 },
          { id: '10', name: 'Contact', href: '/contact', enabled: true, order: 9 },
        ];
        setMenuItems(defaultItems);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo size should be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    try {
      const fileId = ID.unique();
      const result = await storageService.uploadFile(STORAGE_BUCKET, fileId, logoFile);
      if (result.success) {
        return storageService.getFileView(STORAGE_BUCKET, result.data.$id);
      }
      return null;
    } catch (error) {
      console.error('Error uploading logo:', error);
      return null;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let finalLogoUrl = logoUrl;

      if (logoFile) {
        finalLogoUrl = await uploadLogo();
        if (!finalLogoUrl) {
          alert('Failed to upload logo');
          setSaving(false);
          return;
        }
      }

      const settingsData = {
        menuItems: JSON.stringify(menuItems),
        menuItemsFont: menuItemsFont,
        logoUrl: finalLogoUrl,
        cvButton: JSON.stringify(cvButton),
        cvButtonFont: cvButtonFont,
      };

      const existing = await databaseService.listDocuments(MENUBAR_COLLECTION);
      if (existing.success && existing.data.documents.length > 0) {
        await databaseService.updateDocument(
          MENUBAR_COLLECTION,
          existing.data.documents[0].$id,
          settingsData
        );
      } else {
        await databaseService.createDocument(
          MENUBAR_COLLECTION,
          settingsData
        );
      }

      setLogoUrl(finalLogoUrl);
      setLogoFile(null);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMenuNameChange = (id, newName) => {
    setMenuItems(menuItems.map((item) => item.id === id ? { ...item, name: newName } : item));
  };

  const handleMenuHrefChange = (id, newHref) => {
    setMenuItems(menuItems.map((item) => item.id === id ? { ...item, href: newHref } : item));
  };

  const toggleMenuItem = (id) => {
    setMenuItems(menuItems.map((item) => item.id === id ? { ...item, enabled: !item.enabled } : item));
  };

  const moveItem = (index, direction) => {
    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newItems.length) {
      [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
      newItems.forEach((item, idx) => { item.order = idx; });
      setMenuItems(newItems);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-[#4C1D95] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#4C1D95] font-medium">Loading Menubar Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Menubar</h1>
          <p className="text-gray-500 mt-1">Customize your website's navigation bar.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-[#4C1D95] text-white rounded-xl font-medium shadow-lg shadow-purple-900/20 hover:bg-[#3b1674] hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          <span>{saving ? 'Saving Changes...' : 'Save Changes'}</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Left Column: Settings */}
        <div className="space-y-6">
          
          {/* Logo Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 overflow-hidden relative group/card">
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                <ImageIcon className="w-5 h-5" />
              </div>
              Logo Configuration
            </h2>
            
            <div className="flex flex-col sm:flex-row items-center gap-6 p-4 bg-gray-50 rounded-xl border border-gray-100 border-dashed">
              <div className="relative group cursor-pointer w-24 h-24 shrink-0">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="w-full h-full object-contain rounded-lg bg-white shadow-sm p-2"
                  />
                ) : (
                  <div className="w-full h-full bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                    <span className="text-gray-300 text-xs font-medium">No Logo</span>
                  </div>
                )}
                <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg cursor-pointer">
                  <Upload className="w-6 h-6 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Upload Brand Logo</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Recommended size: 80x80px. <br/>Supports PNG, JPG, SVG (Max 2MB).
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer">
                  <Upload className="w-4 h-4 text-[#4C1D95]" />
                  <span>Choose File</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Menu Items Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 relative group/card">
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
            
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                  <Layout className="w-5 h-5" />
                </div>
                Menu Structure
              </h2>
              <FontSelector
                value={menuItemsFont}
                onChange={setMenuItemsFont}
                label=""
                previewText="Menu Font"
                compact
              />
            </div>

            <div className="space-y-3">
              {menuItems
                .sort((a, b) => a.order - b.order)
                .map((item, index) => (
                  <div
                    key={item.id}
                    className={`
                      group flex items-center gap-3 p-3 rounded-xl border transition-all duration-200
                      ${!item.enabled ? 'bg-gray-50 border-gray-100 opacity-75' : 'bg-white border-gray-200 hover:border-[#4C1D95]/30 hover:shadow-sm'}
                    `}
                  >
                    <div className="flex flex-col gap-1 pr-2 border-r border-gray-100">
                      <button
                         onClick={() => moveItem(index, 'up')}
                         disabled={index === 0}
                         className="p-0.5 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20 transition-colors"
                      >
                         <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                         onClick={() => moveItem(index, 'down')}
                         disabled={index === menuItems.length - 1}
                         className="p-0.5 text-gray-400 hover:text-[#4C1D95] disabled:opacity-20 transition-colors"
                      >
                         <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div className="relative">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => handleMenuNameChange(item.id, e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all placeholder:text-gray-300"
                          placeholder="Menu Label"
                        />
                        <Type className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={item.href}
                          onChange={(e) => handleMenuHrefChange(item.id, e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all placeholder:text-gray-300 font-mono text-xs"
                          placeholder="/path"
                        />
                        <LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      </div>
                    </div>

                    <button
                      onClick={() => toggleMenuItem(item.id)}
                      className={`
                        p-2 rounded-lg transition-all
                        ${item.enabled ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}
                      `}
                      title={item.enabled ? "Visible" : "Hidden"}
                    >
                      {item.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* CTA Button Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 relative group/card">
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#4C1D95] to-purple-400 opacity-0 group-hover/card:opacity-100 transition-opacity" />
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <div className="p-2 bg-purple-50 rounded-lg text-[#4C1D95]">
                <MousePointer2 className="w-5 h-5" />
              </div>
              Action Button
            </h2>

            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Button Text</label>
                  <input
                    type="text"
                    value={cvButton.text}
                    onChange={(e) => setCvButton({ ...cvButton, text: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Link URL</label>
                  <input
                    type="text"
                    value={cvButton.link}
                    onChange={(e) => setCvButton({ ...cvButton, link: e.target.value })}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <FontSelector
                  value={cvButtonFont}
                  onChange={setCvButtonFont}
                  label="Button Font"
                  previewText={cvButton.text}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Live Preview */}
        <div className="lg:sticky lg:top-8 space-y-4">
          <div className="flex items-center justify-between">
             <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Live Preview</h3>
             <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">Interactive</span>
          </div>
          
          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xl bg-white">
            {/* Browser frame decoration */}
            <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/80" />
                <div className="w-3 h-3 rounded-full bg-amber-400/80" />
                <div className="w-3 h-3 rounded-full bg-green-400/80" />
              </div>
              <div className="flex-1 mx-4 bg-white border border-gray-200 rounded-md h-6 text-[10px] text-gray-400 flex items-center px-2 font-mono">
                your-portfolio.com
              </div>
            </div>

            {/* Navbar Preview */}
            <div className="bg-white p-6 min-h-[400px] flex flex-col relative bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]">
              
              <nav className="flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-md rounded-2xl border border-gray-100 shadow-sm sticky top-0 z-10">
                {/* Logo */}
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="h-10 w-auto object-contain" />
                  ) : (
                    <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-400 font-bold border border-gray-200 border-dashed">
                       LOGO
                    </div>
                  )}
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center justify-center gap-6 px-4">
                  {menuItems
                    .filter(item => item.enabled)
                    .sort((a, b) => a.order - b.order)
                    .map((item) => (
                      <span
                        key={item.id}
                        className="text-sm text-gray-600 font-medium hover:text-[#4C1D95] cursor-pointer transition-colors relative group"
                        style={{ fontFamily: menuItemsFont }}
                      >
                        {item.name}
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#4C1D95] transition-all group-hover:w-full" />
                      </span>
                    ))}
                </div>

                {/* CTA Button */}
                <div className="flex-shrink-0">
                  <a
                    href={cvButton.link}
                    className="inline-flex items-center justify-center px-5 py-2 text-sm font-medium text-white transition-all transform rounded-full hover:shadow-lg hover:-translate-y-0.5"
                    style={{
                      fontFamily: cvButtonFont,
                      background: 'linear-gradient(135deg, #4C1D95 0%, #7c3aed 100%)',
                    }}
                    onClick={(e) => e.preventDefault()}
                  >
                    {cvButton.text}
                  </a>
                </div>
              </nav>

              {/* Mock Content */}
              <div className="mt-8 space-y-6 opacity-30 pointer-events-none">
                 <div className="h-40 bg-gray-200 rounded-2xl w-full"></div>
                 <div className="grid grid-cols-3 gap-4">
                    <div className="h-32 bg-gray-200 rounded-xl"></div>
                    <div className="h-32 bg-gray-200 rounded-xl"></div>
                    <div className="h-32 bg-gray-200 rounded-xl"></div>
                 </div>
              </div>

            </div>
          </div>
          
          <div className="p-4 bg-blue-50 text-blue-800 rounded-xl text-sm border border-blue-100 flex items-start gap-3">
             <div className="p-1 bg-blue-100 rounded-full mt-0.5 shrink-0">
                <Layout className="w-3.5 h-3.5 text-blue-600" />
             </div>
             <div>
                <p className="font-semibold mb-0.5">Responsive Design</p>
                <p className="opacity-90 leading-relaxed">The menu automatically adapts to mobile text links or a hamburger menu depending on screen size. Resize your browser window to test responsiveness.</p>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}
